import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  AccountBalance as BalanceIcon,
  TrendingUp as DebitIcon,
  TrendingDown as CreditIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';

import {
  SupplierLedgerService,
  SupplierLedgerResponse,
  SupplierLedgerSummary,
  SupplierLedgerFilters,
  TransactionType,
} from '../services/supplierLedgerService';
import PageHeader from '../components/ui/PageHeader';

interface Supplier {
  id: string;
  business_name: string;
  contact_person?: string;
  gstin?: string;
  phone: string;
  email?: string;
}

const SupplierLedgerPage: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [ledgerData, setLedgerData] = useState<SupplierLedgerResponse | null>(null);
  const [ledgerSummary, setLedgerSummary] = useState<SupplierLedgerSummary | null>(null);
  const [transactionTypes, setTransactionTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [filters, setFilters] = useState<SupplierLedgerFilters>({
    page: 1,
    page_size: 25,
  });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedTransactionType, setSelectedTransactionType] = useState<string>('');

  // Load initial data
  useEffect(() => {
    if (!supplierId) {
      navigate('/admin/suppliers');
      return;
    }
    loadInitialData();
  }, [supplierId]);

  // Load ledger data when filters change
  useEffect(() => {
    if (supplierId) {
      loadLedgerData();
    }
  }, [filters, supplierId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load transaction types and initial ledger data
      const [transactionTypesData, ledgerSummaryData] = await Promise.all([
        SupplierLedgerService.getTransactionTypes(),
        SupplierLedgerService.getSupplierLedgerSummary(supplierId!),
      ]);

      setTransactionTypes(transactionTypesData.transaction_types);
      setLedgerSummary(ledgerSummaryData);
    } catch (err: any) {
      console.error('Error loading initial data:', err);
      setError(err.response?.data?.detail || 'Failed to load supplier data');
      enqueueSnackbar('Failed to load supplier data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadLedgerData = async () => {
    try {
      const ledgerFilters: SupplierLedgerFilters = {
        ...filters,
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        transaction_type: selectedTransactionType as keyof TransactionType || undefined,
      };

      const data = await SupplierLedgerService.getSupplierLedger(supplierId!, ledgerFilters);
      setLedgerData(data);
      
      // Extract supplier info from response
      if (data.supplier_info) {
        setSupplier(data.supplier_info as Supplier);
      }
    } catch (err: any) {
      console.error('Error loading ledger data:', err);
      enqueueSnackbar('Failed to load ledger data', { variant: 'error' });
    }
  };

  const handleRefresh = () => {
    loadInitialData();
  };

  const handleFilterChange = (newFilters: Partial<SupplierLedgerFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      
      // For now, we'll export as CSV since PDF endpoint might not be implemented
      // If PDF endpoint exists, uncomment the code below
      /*
      const blob = await SupplierLedgerService.downloadLedgerPdf(supplierId!, {
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        transaction_type: selectedTransactionType as keyof TransactionType || undefined,
        include_summary: true,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `supplier_ledger_${supplier?.business_name?.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Ledger PDF downloaded successfully', { variant: 'success' });
      */
      
      enqueueSnackbar('PDF export is not yet implemented. Please use CSV export.', { variant: 'info' });
    } catch (err: any) {
      console.error('Error exporting PDF:', err);
      enqueueSnackbar('Failed to export PDF', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      
      // Get all ledger entries for CSV export
      const allEntries = await SupplierLedgerService.getSupplierLedger(supplierId!, {
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        transaction_type: selectedTransactionType as keyof TransactionType || undefined,
        page: 1,
        page_size: 10000, // Get all entries
      });

      // Convert to CSV
      const csvHeaders = [
        'Date',
        'Type',
        'Reference',
        'Description',
        'Debit Amount',
        'Credit Amount',
        'Balance'
      ];

      const csvRows = allEntries.entries.map(entry => [
        format(parseISO(entry.transaction_date), 'dd/MM/yyyy'),
        entry.transaction_type.replace('_', ' ').toUpperCase(),
        entry.reference_number,
        entry.description || '',
        entry.debit_amount > 0 ? entry.debit_amount.toFixed(2) : '',
        entry.credit_amount > 0 ? entry.credit_amount.toFixed(2) : '',
        entry.balance.toFixed(2)
      ]);

      // Add summary if available
      if (allEntries.balance_summary) {
        csvRows.unshift([]); // Empty row
        csvRows.unshift(['SUMMARY', '', '', '', '', '', '']);
        csvRows.unshift(['Opening Balance', '', '', '', '', '', allEntries.balance_summary.opening_balance.toFixed(2)]);
        csvRows.unshift(['Total Debits', '', '', '', allEntries.balance_summary.total_debits.toFixed(2), '', '']);
        csvRows.unshift(['Total Credits', '', '', '', '', allEntries.balance_summary.total_credits.toFixed(2), '']);
        csvRows.unshift(['Closing Balance', '', '', '', '', '', allEntries.balance_summary.closing_balance.toFixed(2)]);
        csvRows.unshift([]); // Empty row
      }

      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `supplier_ledger_${supplier?.business_name?.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Ledger CSV downloaded successfully', { variant: 'success' });
    } catch (err: any) {
      console.error('Error exporting CSV:', err);
      enqueueSnackbar('Failed to export CSV', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getTransactionTypeChip = (type: string) => {
    const typeConfig = {
      invoice: { label: 'Invoice', color: 'error' as const },
      payment: { label: 'Payment', color: 'success' as const },
      credit_note: { label: 'Credit Note', color: 'info' as const },
      debit_note: { label: 'Debit Note', color: 'warning' as const },
      opening_balance: { label: 'Opening Balance', color: 'default' as const },
      adjustment: { label: 'Adjustment', color: 'secondary' as const },
    };

    const config = typeConfig[type as keyof typeof typeConfig] || { label: type, color: 'default' as const };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'error.main'; // Debit (we owe supplier)
    if (balance < 0) return 'success.main'; // Credit (supplier owes us)
    return 'text.primary'; // Zero balance
  };

  const getBalanceIcon = (balance: number) => {
    if (balance > 0) return <DebitIcon color="error" />;
    if (balance < 0) return <CreditIcon color="success" />;
    return <BalanceIcon />;
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 120,
      renderCell: (params) => format(parseISO(params.value), 'dd/MM/yyyy'),
      sortable: true,
    },
    {
      field: 'transaction_type',
      headerName: 'Type',
      width: 140,
      renderCell: (params) => getTransactionTypeChip(params.value),
    },
    {
      field: 'reference_number',
      headerName: 'Reference',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'debit_amount',
      headerName: 'Debit',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" color={params.value > 0 ? 'error.main' : 'text.disabled'}>
          {params.value > 0 ? formatCurrency(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'credit_amount',
      headerName: 'Credit',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" color={params.value > 0 ? 'success.main' : 'text.disabled'}>
          {params.value > 0 ? formatCurrency(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 140,
      align: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getBalanceIcon(params.value)}
          <Typography variant="body2" color={getBalanceColor(params.value)} fontWeight="medium">
            {formatCurrency(Math.abs(params.value))}
          </Typography>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading supplier ledger...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <PageHeader
          title={`Supplier Ledger - ${supplier?.business_name || 'Loading...'}`}
          subtitle={`Account statement and transaction history`}
          actions={[
            {
              label: 'Refresh',
              icon: <RefreshIcon />,
              onClick: handleRefresh,
              variant: 'outlined'
            },
            {
              label: exporting ? 'Exporting...' : 'Export PDF',
              icon: <PdfIcon />,
              onClick: handleExportPdf,
              disabled: exporting,
              variant: 'contained'
            },
            {
              label: 'Export CSV',
              icon: <CsvIcon />,
              onClick: handleExportCsv,
              disabled: exporting,
              variant: 'outlined'
            },
            {
              label: 'Back to Suppliers',
              icon: <ArrowBackIcon />,
              onClick: () => navigate('/admin/suppliers'),
              variant: 'outlined'
            }
          ]}
        />

        {/* Balance Summary Cards */}
        {ledgerSummary && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Current Payable Balance
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getBalanceIcon(ledgerSummary.current_balance)}
                    <Typography variant="h5" color={getBalanceColor(ledgerSummary.current_balance)}>
                      {formatCurrency(Math.abs(ledgerSummary.current_balance))}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {ledgerSummary.current_balance > 0 ? 'We Owe Supplier' : 
                     ledgerSummary.current_balance < 0 ? 'Supplier Owes Us' : 'Balanced'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Transactions
                  </Typography>
                  <Typography variant="h5">
                    {ledgerSummary.total_transactions}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    All time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Latest Transaction
                  </Typography>
                  <Typography variant="h6">
                    {ledgerSummary.latest_transaction_date 
                      ? format(parseISO(ledgerSummary.latest_transaction_date), 'dd/MM/yyyy')
                      : 'No transactions'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Transaction Types
                  </Typography>
                  <Stack spacing={0.5}>
                    {Object.entries(ledgerSummary.transaction_counts).map(([type, count]) => (
                      <Typography key={type} variant="caption">
                        {type.replace('_', ' ')}: {count}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon />
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={selectedTransactionType}
                  onChange={(e) => setSelectedTransactionType(e.target.value)}
                  input={<OutlinedInput label="Transaction Type" />}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {transactionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setSelectedTransactionType('');
                  handleFilterChange({ page: 1 });
                }}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Balance Summary from Current Filter */}
        {ledgerData?.balance_summary && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Period Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Opening Balance</Typography>
                <Typography variant="h6" color={getBalanceColor(ledgerData.balance_summary.opening_balance)}>
                  {formatCurrency(ledgerData.balance_summary.opening_balance)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Total Debits</Typography>
                <Typography variant="h6" color="error.main">
                  {formatCurrency(ledgerData.balance_summary.total_debits)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Total Credits</Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(ledgerData.balance_summary.total_credits)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Closing Balance</Typography>
                <Typography variant="h6" color={getBalanceColor(ledgerData.balance_summary.closing_balance)}>
                  {formatCurrency(ledgerData.balance_summary.closing_balance)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Ledger Entries Table */}
        <Paper sx={{ height: 600 }}>
          <DataGrid
            rows={ledgerData?.entries || []}
            columns={columns}
            loading={!ledgerData}
            paginationMode="server"
            rowCount={ledgerData?.total_count || 0}
            paginationModel={{ 
              page: (filters.page || 1) - 1, 
              pageSize: filters.page_size || 25 
            }}
            onPaginationModelChange={(model) => {
              handleFilterChange({
                page: model.page + 1,
                page_size: model.pageSize,
              });
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default SupplierLedgerPage;
